import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Slider,
  TextField,
  Grid,
  Button,
  Collapse,
  Card,
  CardContent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ShowChart,
  FilterList,
} from '@mui/icons-material';
import { WidgetProps } from '../types/index.js';
import { useWidgetData } from '../hooks/useWidgetData.js';
import { marketAnalysisApi, VolatilityLiquidityResponse } from '../../../services/marketAnalysis.js';

const formatNumber = (num: number): string => {
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toFixed(2);
};

const formatCurrency = (num: number): string => {
  return `$${formatNumber(num)}`;
};

const formatPercent = (num: number): string => {
  return `${num.toFixed(2)}%`;
};

export const VolatilityLiquidityWidget: React.FC<WidgetProps> = ({
  widget,
  onUpdateOutputs,
  onUpdateState,
  onUpdateConfig,
}) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [atrRange, setAtrRange] = useState<[number, number]>([0, 10]);
  const [atrPercentRange, setAtrPercentRange] = useState<[number, number]>([0, 10]);
  const [minVolume, setMinVolume] = useState<number>(100000);
  const [maxSpread, setMaxSpread] = useState<number>(5);
  const [period, setPeriod] = useState<number>(14);
  
  // Get filter values from config or use defaults
  const filters = {
    atrMin: widget.config.atrMin ?? atrRange[0],
    atrMax: widget.config.atrMax ?? atrRange[1],
    atrPercentMin: widget.config.atrPercentMin ?? atrPercentRange[0],
    atrPercentMax: widget.config.atrPercentMax ?? atrPercentRange[1],
    avgVolumeMin: widget.config.avgVolumeMin ?? minVolume,
    bidAskSpreadMax: widget.config.bidAskSpreadMax ?? maxSpread,
    period: widget.config.period ?? period,
    limit: widget.config.limit ?? 50,
  };

  // Fetch function for the hook
  const fetchVolatilityData = useCallback(async () => {
    const data = await marketAnalysisApi.getVolatilityLiquidityFilter(filters);
    
    // Update outputs for other widgets to consume
    if (data) {
      onUpdateOutputs({
        stocks: data.filtered,
        marketStats: data.marketStats,
        timestamp: data.timestamp,
      });
    }
    
    return data;
  }, [JSON.stringify(filters), onUpdateOutputs]);

  // Use the widget data hook
  const { 
    data, 
    loading, 
    error, 
    refresh 
  } = useWidgetData<VolatilityLiquidityResponse>({
    widget,
    onUpdateState,
    fetchData: fetchVolatilityData,
    refreshInterval: 300000, // 5 minutes
    initialData: {
      filtered: [],
      marketStats: {
        avgATR: 0,
        avgATRPercent: 0,
        avgVolume: 0,
        totalScanned: 0,
        totalFiltered: 0,
      },
      timestamp: new Date().toISOString(),
    },
  });

  // Apply filters
  const handleApplyFilters = () => {
    onUpdateConfig({
      ...widget.config,
      atrMin: atrRange[0],
      atrMax: atrRange[1],
      atrPercentMin: atrPercentRange[0],
      atrPercentMax: atrPercentRange[1],
      avgVolumeMin: minVolume,
      bidAskSpreadMax: maxSpread,
      period: period,
    });
    refresh();
  };

  const handleRowClick = (symbol: string) => {
    setExpandedRow(expandedRow === symbol ? null : symbol);
  };

  const getVolatilityColor = (rank: number): string => {
    if (rank >= 80) return '#ff1744'; // High volatility
    if (rank >= 60) return '#ff9800'; // Medium-high
    if (rank >= 40) return '#ffc107'; // Medium
    if (rank >= 20) return '#4caf50'; // Low-medium
    return '#2196f3'; // Low volatility
  };

  const getLiquidityColor = (score: number): string => {
    if (score >= 80) return '#4caf50'; // Excellent
    if (score >= 60) return '#8bc34a'; // Good
    if (score >= 40) return '#ffc107'; // Fair
    if (score >= 20) return '#ff9800'; // Poor
    return '#ff1744'; // Very poor
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Error loading volatility data: {error}</Typography>
        <Button onClick={refresh} startIcon={<RefreshIcon />} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShowChart /> Volatility & Liquidity Filter
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={() => setShowFilters(!showFilters)} size="small">
              <FilterList />
            </IconButton>
            <IconButton onClick={refresh} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Market Stats */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
          <Chip 
            label={`Scanned: ${data?.marketStats.totalScanned || 0}`} 
            size="small" 
            variant="outlined" 
          />
          <Chip 
            label={`Filtered: ${data?.marketStats.totalFiltered || 0}`} 
            size="small" 
            color="primary" 
          />
          <Chip 
            label={`Avg ATR: ${formatPercent(data?.marketStats.avgATRPercent || 0)}`} 
            size="small" 
            variant="outlined" 
          />
          <Chip 
            label={`Avg Volume: ${formatNumber(data?.marketStats.avgVolume || 0)}`} 
            size="small" 
            variant="outlined" 
          />
        </Box>
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Card sx={{ m: 2 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>ATR Range ($)</Typography>
                <Slider
                  value={atrRange}
                  onChange={(_, value) => setAtrRange(value as [number, number])}
                  valueLabelDisplay="auto"
                  min={0}
                  max={20}
                  step={0.5}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography gutterBottom>ATR Percent Range (%)</Typography>
                <Slider
                  value={atrPercentRange}
                  onChange={(_, value) => setAtrPercentRange(value as [number, number])}
                  valueLabelDisplay="auto"
                  min={0}
                  max={10}
                  step={0.1}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Min Volume"
                  type="number"
                  value={minVolume}
                  onChange={(e) => setMinVolume(parseInt(e.target.value) || 0)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Max Spread %"
                  type="number"
                  value={maxSpread}
                  onChange={(e) => setMaxSpread(parseFloat(e.target.value) || 0)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="ATR Period"
                  type="number"
                  value={period}
                  onChange={(e) => setPeriod(parseInt(e.target.value) || 14)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Button 
                  variant="contained" 
                  onClick={handleApplyFilters}
                  disabled={loading}
                  fullWidth
                >
                  Apply Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Data Table */}
      <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">ATR</TableCell>
              <TableCell align="right">ATR %</TableCell>
              <TableCell align="center">Vol Rank</TableCell>
              <TableCell align="right">Volume</TableCell>
              <TableCell align="center">Liquidity</TableCell>
              <TableCell align="right">Spread</TableCell>
              <TableCell width={40}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.filtered.map((stock) => (
              <React.Fragment key={stock.symbol}>
                <TableRow 
                  hover 
                  onClick={() => handleRowClick(stock.symbol)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {stock.symbol}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(stock.price)}</TableCell>
                  <TableCell align="right">{formatCurrency(stock.atr)}</TableCell>
                  <TableCell align="right">{formatPercent(stock.atrPercent)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={stock.volatilityRank}
                      size="small"
                      sx={{ 
                        backgroundColor: getVolatilityColor(stock.volatilityRank),
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{formatNumber(stock.avgVolume30Day)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={stock.liquidityScore}
                      size="small"
                      sx={{ 
                        backgroundColor: getLiquidityColor(stock.liquidityScore),
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{formatPercent(stock.bidAskSpreadPercent)}</TableCell>
                  <TableCell>
                    <IconButton size="small">
                      {expandedRow === stock.symbol ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={expandedRow === stock.symbol} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2" color="textSecondary">
                              Recent Volatility
                            </Typography>
                            <Typography variant="body2">
                              Daily: {formatPercent(stock.recentVolatility.daily)}
                            </Typography>
                            <Typography variant="body2">
                              Weekly: {formatPercent(stock.recentVolatility.weekly)}
                            </Typography>
                            <Typography variant="body2">
                              Monthly: {formatPercent(stock.recentVolatility.monthly)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2" color="textSecondary">
                              Liquidity Details
                            </Typography>
                            <Typography variant="body2">
                              Dollar Volume: {formatCurrency(stock.avgDollarVolume)}
                            </Typography>
                            <Typography variant="body2">
                              Bid-Ask Spread: {formatCurrency(stock.bidAskSpread)}
                            </Typography>
                            {stock.optionsVolume && (
                              <Typography variant="body2">
                                Options Volume: {formatNumber(stock.optionsVolume)}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="subtitle2" color="textSecondary">
                              Trading Metrics
                            </Typography>
                            <Typography variant="body2">
                              Volatility Percentile: {stock.volatilityRank}th
                            </Typography>
                            <Typography variant="body2">
                              Liquidity Score: {stock.liquidityScore}/100
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};